

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Link } from 'react-router';
import styles from './RegionEAEEmbed.css';
import withStyles from '../../decorators/withStyles';
import {simplePlaceName} from "../../utils/utils";
import {sameLocationNewHash} from "../../utils/utils";
import ComponentIcons from "../ComponentIcons";
import AnchoredComponent from "../AnchoredComponent";
import BarChart from "../BarChart";
import PresetButtons from "../PresetButtons";
import RegionEAEData from "../RegionEAEData";
import Modal from "../Modal";
import ReactDOM from 'react-dom';
import {SetModalVisible} from "../../actions";
import { capitalize, toFixed, commas, xlt } from "../../utils/utils";
import { connect } from "react-redux";
import { compose } from 'recompose';
import * as d3 from 'd3';


function filterData(data, props, predicate) {
  if (predicate) {
    return data.find(d => predicate(d));
  } else {
    return data.find(d => {
      return (d.scenario === props.eaeScenario &&
      d.percentile === props.eaePercentile &&
      d.variable === props.eaeVariable)
    });
  }
}

function getEAE(d) {
  return t => d.logistic_param_t0 + (d.logistic_param_t1 - d.logistic_param_t0) /
  (1 + Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)));
}

function getEAEdydx(d) {
  return t => d.logistic_param_k * (d.logistic_param_t1 - d.logistic_param_t0) * (
  Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)) / Math.pow(Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)) + 1, 2))
}

function varName(variable) {
  if (variable === "Population") {
    return "population";
  } else if (variable === "Housing") {
    return "housing";
  } else if (variable === "Roads") {
    return "road miles";
  } else if (variable === "Property") {
    return "property value";
  }
}

const RegionEAESlide = ({data, totals, placeName, eaeDerivative, eaeIsPct, eaeVariable, eaeScenario, eaePercentile}) => {

  if (data.length === 0 || totals.length === 0) {
    return <span></span>;
  }

  const props = {
    eaeDerivative: eaeDerivative !== "false",
    eaeIsPct: eaeIsPct !== "false",
    eaeVariable,
    eaeScenario: eaeScenario.replace('%20', ' '),
    eaePercentile: +eaePercentile
  };
  const filtered = filterData(data, props);
  const eaeF = getEAE(filtered);
  const eaeFprime = getEAEdydx(filtered);
  const xRange = d3.range(2000, 2101, 10);
  var graphData = xRange.map(year => {return {
    x: year,
    y: eaeDerivative ? eaeFprime(year) * 10 : eaeF(year),
    text: `${eaeDerivative ? Math.round(10000 * eaeFprime(year) * 10) / 100 : (Math.round(1000 * eaeF(year)) / 10)}%`
  }});
  const dataMax = filterData(data, props, d => {
    return d.variable === eaeVariable && d.scenario === "RCP 8.5" && d.percentile === 95
  });
  const eaeFMax = getEAE(dataMax);
  const eaeFprimeMax = getEAEdydx(dataMax);

  var graphMax = d3.max(xRange.map(year =>  eaeDerivative ? eaeFprimeMax(year) : eaeFMax(year)));
  if (!eaeIsPct) {
    graphData = graphData.map(pt => { return {
      x: pt.x,
      y: pt.y * totals[eaeVariable],
      text: eaeDerivative ? commas(Math.round(10 * pt.y * totals[eaeVariable]) / 10)
          : commas(Math.round(pt.y * totals[eaeVariable]))
    }});
    graphMax *= totals[eaeVariable];
  }
  const chartMargin = {top: 20, right: 20, bottom: 60, left: 88};
  if (!(eaeDerivative && eaeIsPct) && (eaeIsPct || graphMax < 1000)) {
    chartMargin.left = 70;
  }
  if (graphMax > 100000) {
    chartMargin.left = 105;
  }
  const width = 780 - chartMargin.left - chartMargin.right;
  const height = 540 - chartMargin.top - chartMargin.bottom;

  var scenarioText;
  if (eaeScenario === "RCP 2.6") {
    scenarioText = "assuming extreme cuts in heat-trapping pollution (RCP 2.6)";
  } else if (eaeScenario === "RCP 4.5") {
    scenarioText = "assuming moderate cuts in heat-trapping pollution (RCP 4.5)";
  } else if (eaeScenario === "RCP 8.5") {
    scenarioText = "assuming unchecked pollution (RCP 8.5)";
  } else {
    scenarioText = "";
  }

  var percentileText;
  if (eaePercentile === 50) {
    percentileText = "median";
  } else {
    percentileText = `${eaePercentile}th percentile`;
  }

  var yLabel;
  if (!eaeIsPct && eaeVariable === "Property") {
    graphData = graphData.map(pt => {return {
      x: pt.x,
      y: pt.y / 1000000,
      text: `$${commas(Math.round(pt.y / 1000000))} million`
    }});
    graphMax /= 1000000;
    yLabel = `${capitalize(varName(eaeVariable))} ($M)${eaeDerivative ? ": growth rate per decade" : ""}`;
  } else {
    yLabel = `${eaeIsPct ?
        `Percentage of ${varName(eaeVariable)}` :
        capitalize(varName(eaeVariable))}${eaeDerivative ? ": growth rate per decade" : ""}`;
  }

  const setTxContent = (el) => d3.select(el).attr('tx-content', 'block');

  const headerText = () => {
    if (eaeDerivative) {
      return eaeIsPct ? 'Growth in expected percentage of' : 'Growth in expected total';
    } else {
      return eaeIsPct ? 'Percentage of' : 'Total';
    }
  };

  return (
      <section className="RegionEAESlide">
        <div className="header">
          <h1>Projected Growth in Flood Exposure</h1>
        </div>
        <h2 className="header">
          <span>{headerText()}</span>
          <var> {xlt(props.eaeVariable.toLowerCase())}</var>
          <span> in </span>
          <var>{xlt(simplePlaceName(placeName))} </var>
          <span>exposed to flooding</span>
        </h2>
        <BarChart
            id="region-eae-embed-chart"
            data={graphData}
            yMax={graphMax}
            isWaterLevel={!eaeIsPct}
            margin={chartMargin}
            width={width}
            height={height}
            xLabel={xlt("Year")}
            yLabel={xlt(yLabel)}
        />
        <p class="map-explain">
          <span>Main source:</span>
          <span>&nbsp;</span>
          <span><a href="http://dx.doi.org/10.1007/s10584-017-1963-7" target="_blank">Kulp and Strauss 2017</a>, Climatic Change.</span>
          <span>&nbsp;</span>
          <span>Sea level projections:</span>
          <span>&nbsp;</span>
          <span ref={setTxContent}>
            <var>{xlt(percentileText)} </var>
            <span>local projections</span>
            <var> {xlt(scenarioText)}.</var>
          </span>
          <span>&nbsp;</span>
          <span>(<a href="http://onlinelibrary.wiley.com/doi/10.1002/2014EF000239/abstract" target="_blank">Kopp et al. 2014</a>, Earth's Future.)</span>
        </p>
        <div className="cc-logo-img" />
      </section>
  )
};


class RegionEAEEmbed extends AnchoredComponent {

  render() {
    //const {margin, width, height} = this.props;
    ////const margin = {top: 20, right: 20, bottom: 60, left: 75};
    ////const width = 780 - margin.left - margin.right;
    ////const height = 540 - margin.top - margin.bottom;

    return (<div className="RegionEAEEmbed band white">
      <section>
        <RegionEAEData
            placeType={this.props.place.type}
            placeID={this.props.place.id}
        >
          <RegionEAESlide />
        </RegionEAEData>
      </section>
      </div>
    )
  }
}

export default compose(
  withStyles(styles),
  connect((state) => state),
)(RegionEAEEmbed);
